import { useCallback, useEffect, useRef, useState } from "react";
import { type LayoutChangeEvent, StyleSheet, View } from "react-native";
import { useAppTheme } from "../../../useAppTheme";

export type SeekBarVariant = "compact" | "full";

export type SeekBarProps = {
	position: number;
	duration: number;
	onSeek: (position: number) => void;
	variant: SeekBarVariant;
};

const clamp = (value: number, min: number, max: number) =>
	Math.min(max, Math.max(min, value));

const handleHalfWidths: Record<SeekBarVariant, number> = {
	compact: 2,
	full: 2,
};

const compactStyles = StyleSheet.create({
	container: {
		width: "100%",
		height: 24,
		justifyContent: "center",
		cursor: "pointer",
	},
	track: { width: "100%", height: 6, borderRadius: 3, overflow: "hidden" },
	activeTrack: {
		height: "100%",
		borderTopLeftRadius: 3,
		borderBottomLeftRadius: 3,
		borderTopRightRadius: 1,
		borderBottomRightRadius: 1,
	},
	handle: { position: "absolute", width: 4, height: 16, borderRadius: 2 },
});

const fullStyles = StyleSheet.create({
	container: {
		width: "100%",
		height: 44,
		justifyContent: "center",
		cursor: "pointer",
	},
	track: { width: "100%", height: 16, borderRadius: 16, overflow: "hidden" },
	activeTrack: {
		height: "100%",
		borderTopLeftRadius: 16,
		borderBottomLeftRadius: 16,
		borderTopRightRadius: 2,
		borderBottomRightRadius: 2,
	},
	handle: { position: "absolute", width: 4, height: 44, borderRadius: 2 },
});

const stylesByVariant: Record<SeekBarVariant, typeof compactStyles> = {
	compact: compactStyles,
	full: fullStyles,
};

const SeekBar = ({ position, duration, onSeek, variant }: SeekBarProps) => {
	const theme = useAppTheme();
	const styles = stylesByVariant[variant];
	const halfWidth = handleHalfWidths[variant];
	const [trackWidth, setTrackWidth] = useState(0);
	const [dragX, setDragX] = useState(0);
	const [isDragging, setIsDragging] = useState(false);
	const containerRef = useRef<View>(null);
	const containerLeftRef = useRef(0);

	const handleLayout = useCallback((event: LayoutChangeEvent) => {
		setTrackWidth(event.nativeEvent.layout.width);
	}, []);

	useEffect(() => {
		if (!isDragging) {
			const progress = duration > 0 ? position / duration : 0;
			setDragX(clamp(progress * trackWidth, 0, trackWidth));
		}
	}, [duration, isDragging, position, trackWidth]);

	const updateDragPosition = useCallback(
		(x: number) => {
			const nextX = clamp(x, 0, trackWidth);
			setDragX(nextX);
			return nextX;
		},
		[trackWidth],
	);

	const commitSeek = useCallback(
		(nextX: number) => {
			const nextProgress = trackWidth > 0 ? nextX / trackWidth : 0;
			onSeek(nextProgress * duration);
		},
		[duration, onSeek, trackWidth],
	);

	const measureContainer = useCallback(() => {
		const node = containerRef.current as unknown as HTMLElement;
		if (node?.getBoundingClientRect) {
			containerLeftRef.current = node.getBoundingClientRect().left;
		}
	}, []);

	const getRelativeX = useCallback(
		(pageX: number) => pageX - containerLeftRef.current,
		[],
	);

	return (
		<View
			ref={containerRef}
			style={styles.container}
			accessibilityRole="adjustable"
			accessibilityLabel="Seek bar"
			accessibilityValue={{
				min: 0,
				max: Math.floor(duration),
				now: Math.floor(position),
			}}
			onStartShouldSetResponder={() => true}
			onMoveShouldSetResponder={() => true}
			onResponderGrant={(event) => {
				setIsDragging(true);
				measureContainer();
				updateDragPosition(getRelativeX(event.nativeEvent.pageX));
			}}
			onResponderMove={(event) => {
				updateDragPosition(getRelativeX(event.nativeEvent.pageX));
			}}
			onResponderRelease={(event) => {
				const nextX = updateDragPosition(getRelativeX(event.nativeEvent.pageX));
				setIsDragging(false);
				commitSeek(nextX);
			}}
			onResponderTerminate={() => {
				setIsDragging(false);
			}}
		>
			<View
				onLayout={handleLayout}
				style={[
					styles.track,
					{ backgroundColor: theme.colors.secondaryContainer },
				]}
			>
				<View
					style={[
						styles.activeTrack,
						{
							backgroundColor: theme.colors.primary,
							width: dragX,
						},
					]}
				/>
			</View>
			<View
				style={[
					styles.handle,
					{
						backgroundColor: theme.colors.primary,
						transform: [
							{
								translateX: clamp(
									dragX - halfWidth,
									-halfWidth,
									Math.max(trackWidth - halfWidth, -halfWidth),
								),
							},
						],
					},
				]}
			/>
		</View>
	);
};

export default SeekBar;
